/**
 * Memory-only auth store（ADR-0004 §4.2 #3）。
 *
 * - 会话 token **只驻内存**；禁 localStorage / cookie。冷启动必须重新走 initData 换 token。
 * - 订阅模型：简易 pub/sub，`useSyncExternalStore` 消费。
 */

export interface AuthUser {
  id: string;
  telegramId: string; // BigInt → string（JSON-safe）
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  languageCode: string | null;
  photoUrl: string | null;
  preferredLocale: string | null;
}

export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'error';

export interface AuthState {
  status: AuthStatus;
  token: string | null;
  user: AuthUser | null;
  /** Last error code when status='error' */
  errorCode: string | null;
  /** Last error human-readable message */
  errorMessage: string | null;
  /** traceId of the last server response, for on-screen debugging */
  traceId: string | null;
}

const initialState: AuthState = {
  status: 'idle',
  token: null,
  user: null,
  errorCode: null,
  errorMessage: null,
  traceId: null,
};

let state: AuthState = initialState;
const subscribers = new Set<() => void>();

export function getAuthState(): AuthState {
  return state;
}

export function setAuthState(patch: Partial<AuthState>): void {
  state = { ...state, ...patch };
  subscribers.forEach((fn) => fn());
}

export function resetAuthState(): void {
  state = initialState;
  subscribers.forEach((fn) => fn());
}

export function subscribeAuthState(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/** Test-only: hard reset, clears subscribers. DO NOT call in production code paths. */
export function __resetAuthStoreForTest(): void {
  state = initialState;
  subscribers.clear();
}
